import * as React from 'react';
import isNil from 'lodash/isNil';

import { FormField } from 'components/form';
import Input from 'components/inputs/input/Input';
import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';

import { __t, applyTranslations } from 'utils/language';
import { isRequired, maxLength, minLength } from 'utils/validators';
import { getGenderAdditionalItems } from '../../utils';

import './profile-gender-additional.scss';

applyTranslations(require('locales/common/collabary-common.i18n.json'));
applyTranslations(require('./profile-gender-additional.i18n.json'));

import type {
  Data,
  InfluencerProfileForm,
  MixedSelectDropdownItem,
  TextEntry
} from 'types';

interface Props {
  profile: InfluencerProfileForm,
  onChange: (profile: Partial<InfluencerProfileForm>) => void
}

const MIN_LENGTH_SELF_DESCRIBE = 2;
const MAX_LENGTH_SELF_DESCRIBE = 20;
const COMMON_MSG_KEY = 'collabary_common';
const MSG_KEY = 'profile_gender_additional';

const ProfileGenderAdditional: React.FC<Props> = ({
  profile,
  onChange
}) => {
  const handleDropdownChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null,
    selectedItem?: Partial<MixedSelectDropdownItem> | null
  ): void => {
    if (isNil(data)) {
      return;
    }

    const value = selectedItem?.identifier || updatedItems[0].identifier;

    onChange({
      [data.type]: value,
      influencerGenderSelfDescribed: value === 'self_describe'
        ? profile.influencerGenderSelfDescribed
        : null
    });
  };

  const handleFieldChange = (
    event: React.SyntheticEvent<TextEntry>
  ): void => {
    const target = event.currentTarget;
    const type = target.dataset?.type;
    const value = target.value ?? '';

    if (isNil(type)) {
      return;
    }

    onChange({
      [type]: value
    });
  };

  const renderDropdown = (): React.ReactNode => (
    <MixedSelectDropdown
      key="profile-gender-additional__dropdown--influencerGenderAdditional"
      name="influencerGenderAdditional"
      items={getGenderAdditionalItems(profile)}
      placeholder={__t(`${COMMON_MSG_KEY}.basics.choose_one`)}
      className="profile-gender-additional__dropdown"
      withSelectedInList
      onItemsToggle={handleDropdownChange}
      data={{ type: 'influencerGenderAdditional' }}
    />
  );

  const renderInput = (): React.ReactNode => {
    const value = profile.influencerGenderSelfDescribed ?? '';

    return (
      <Input
        value={value}
        name="influencerGenderSelfDescribed"
        className="profile-gender-additional__input"
        onChange={handleFieldChange}
        charCount={{
          curCount: value.length,
          maxChars: MAX_LENGTH_SELF_DESCRIBE,
          showCountOnly: true
        }}
        data={{ type: 'influencerGenderSelfDescribed' }}
      />
    );
  };

  return (
    <div className="profile-gender-additional">
      <FormField
        label={__t(`${MSG_KEY}.labels.add_more`)}
        labelType="highlighted"
        direction="column"
        showValidationIcon={false}
        className="profile-gender-additional__field"
        validators={[
          isRequired(profile.influencerGender)
        ]}
      >
        {renderDropdown()}
      </FormField>
      {
        profile.influencerGenderAdditional === 'self_describe' && (
          <FormField
            label={__t(`${MSG_KEY}.labels.identification`)}
            labelType="highlighted"
            direction="column"
            showRequiredMark
            showValidationIcon={false}
            className="profile-gender-additional__field"
            validators={[
              minLength(profile.influencerGenderSelfDescribed, MIN_LENGTH_SELF_DESCRIBE),
              maxLength(profile.influencerGenderSelfDescribed, MAX_LENGTH_SELF_DESCRIBE)
            ]}
          >
            {renderInput()}
          </FormField>
        )
      }
    </div>
  );
};

export default ProfileGenderAdditional;
